import { FabricObject, util as fabricNativeUtils, Control as FabricControl } from 'fabric';

import {
  ELEMENT_CONTROL_FILL_COLOR,
  ELEMENT_FRAME_SCALE_FACTOR,
} from 'editor/src/component/EditorArea/Spread/Page/MediaElement/config';

class CustomControl extends FabricControl {
  controlShape: 'vertical' | 'horizontal' | 'square' | 'circle';
  override x: number;
  override y: number;

  constructor(options: any) {
    super(options);
    this.controlShape = options.controlShape || options.orientation;
    this.x = options.x;
    this.y = options.y;
  }

  override render(
    ctx: CanvasRenderingContext2D,
    left: number,
    top: number,
    styleOverride: any,
    fabricObject: FabricObject,
  ) {
    const size = styleOverride.cornerSize || fabricObject.cornerSize || FabricObject.prototype.cornerSize;
    ctx.save();
    ctx.translate(left, top);
    ctx.rotate(fabricNativeUtils.degreesToRadians(fabricObject.angle as number));
    ctx.fillStyle = ELEMENT_CONTROL_FILL_COLOR;
    ctx.strokeStyle = styleOverride.cornerStrokeColor || ctx.fillStyle;

    // Calculate appropriate radius based on controlShape
    let radius = 2; // Default radius
    if (this.controlShape === 'vertical' || this.controlShape === 'horizontal') {
      radius = 8;
    }

    // Border adjustment to align with the scaled border
    const borderAdjustment = ELEMENT_FRAME_SCALE_FACTOR / 2;
    const SIZE_ADJUSTMENT = 0.8;

    // Get offset direction based on control position (x/y values from control config)
    // For controls on left edge (x < 0), xOffset should be negative
    // For controls on right edge (x > 0), xOffset should be positive
    // For controls on top edge (y < 0), yOffset should be negative
    // For controls on bottom edge (y > 0), yOffset should be positive
    const xDirection = this.x > 0 ? 1 : -1;
    const yDirection = this.y > 0 ? 1 : -1;

    if (this.controlShape === 'vertical') {
      // Draw rounded rectangle for vertical orientation
      const width = size * SIZE_ADJUSTMENT;
      const height = 2 * size;

      // Apply the adjustment with the correct direction
      const xOffset = xDirection * borderAdjustment;

      this.roundRect(ctx, -width / 2 + xOffset, -height / 2, width, height, radius);
      ctx.fill();
      ctx.stroke();
    } else if (this.controlShape === 'horizontal') {
      // Draw rounded rectangle for horizontal orientation
      const width = 2 * size;
      const height = size * SIZE_ADJUSTMENT;

      // Apply the adjustment with the correct direction
      const yOffset = yDirection * borderAdjustment;

      this.roundRect(ctx, -width / 2, -height / 2 + yOffset, width, height, radius);
      ctx.fill();
      ctx.stroke();
    } else if (this.controlShape === 'square') {
      // Draw square with rounded corners for corner controls
      const newSize = size * 1.2;

      // For corner controls, apply adjustments in both directions
      const xOffset = xDirection * borderAdjustment * 0.7;
      const yOffset = yDirection * borderAdjustment * 0.7;

      this.roundRect(ctx, -newSize / 2 + xOffset, -newSize / 2 + yOffset, newSize, newSize, radius);
      ctx.fill();
      ctx.stroke();
    } else if (this.controlShape === 'circle') {
      // Draw circle for rotation control
      const circleRadius = size * 0.75;

      ctx.beginPath();
      ctx.arc(0, 0, circleRadius, 0, Math.PI * 2, false);
      ctx.fill();
      ctx.stroke();
    }

    ctx.restore();
  }

  /**
   * Helper method to draw a rounded rectangle
   */
  private roundRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radiusParam: number,
  ) {
    // Ensure radius is not too large for the rectangle
    const radius = Math.min(radiusParam, Math.min(width / 2, height / 2));

    ctx.roundRect(x, y, width, height, radius);
  }
}

export default CustomControl;
